import { inject, Injectable } from '@angular/core'
import { OAuthService } from 'angular-oauth2-oidc'
import { BehaviorSubject, filter } from 'rxjs'
import { Router, UrlSegment } from '@angular/router'

@Injectable({
  providedIn: 'root',
})
export class AuthDomain {
  private readonly oAuthService = inject(OAuthService)
  private readonly router = inject(Router)

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false)
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable()

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false)
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable()

  constructor() {
    this.oAuthService.events.subscribe(() => {
      this.isAuthenticatedSubject$.next(this.hasValidTokens())
    })
    this.isAuthenticatedSubject$.next(this.hasValidTokens())

    this.oAuthService.events
      .pipe(filter((e) => ['token_received'].includes(e.type)))
      .subscribe(() => this.oAuthService.loadUserProfile())

    this.oAuthService.events
      .pipe(filter((e) => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(() => this.login())
  }

  // Actions
  logOut(): void {
    this.oAuthService.logOut({
      client_id: this.oAuthService.clientId,
      redirect_uri: this.oAuthService.redirectUri,
      response_type: 'code',
    })
  }

  public runInitialLoginSequence(): Promise<void> {
    return this.oAuthService
      .tryLogin()
      .then(() => {
        if (this.oAuthService.hasValidAccessToken()) {
          return Promise.resolve()
        }

        return this.oAuthService
          .silentRefresh()
          .then(() => Promise.resolve())
          .catch((result) => {
            return Promise.reject(result)
          })
      })

      .then(() => {
        this.isDoneLoadingSubject$.next(true)
        if (this.oAuthService.state && this.oAuthService.state !== 'undefined' && this.oAuthService.state !== 'null') {
          let stateUrl = this.oAuthService.state
          if (stateUrl.startsWith('/') === false) {
            stateUrl = decodeURIComponent(stateUrl)
          }
          console.log(`There was state of ${this.oAuthService.state}, so we are sending you to: ${stateUrl}`)
          if (this.router.url !== stateUrl) {
            this.router.navigateByUrl(stateUrl)
          }
        }
      })
      .catch(() => this.isDoneLoadingSubject$.next(true))
  }

  public login(targetUrl?: UrlSegment[]): void {
    this.oAuthService.initLoginFlow(targetUrl?.join('/') || this.router.url)
  }

  public hasValidTokens(): boolean {
    return this.oAuthService.hasValidAccessToken() && this.oAuthService.hasValidIdToken()
  }
}
